import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Flame, ArrowLeft, Save, Moon } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { usePlayer } from "@/context/PlayerContext";
import { Header } from "@/components/Header";
import { PlayerBar } from "@/components/PlayerBar";
import { SongRow } from "@/components/SongRow";
import { LampFlame } from "@/components/BlessingOverlay";
import { getSettings, saveSettings, greeting, blessingLine, todaysLamp, TRADITIONS } from "@/lib/blessing";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputStyle = { background: "var(--bg)", border: "2px solid var(--border)", color: "var(--text)" };

export default function EveningBlessing() {
  const { t, lang } = useLang();
  const { playIndex, current } = usePlayer();
  const [songs, setSongs] = useState([]);
  const [settings, setSettings] = useState(getSettings());

  useEffect(() => { axios.get(`${API}/songs`).then((r) => setSongs(r.data)).catch(() => {}); }, []);

  const lamp = todaysLamp(songs);
  const save = () => { saveSettings(settings); toast.success(t("eb_saved")); };
  const light = () => { if (lamp.length) playIndex(0, lamp); };

  return (
    <div className="min-h-screen pb-32" style={{ background: "var(--bg)" }}>
      <Header />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        <div className="rounded-3xl p-8 text-center sk-fade-up" style={{ background: "var(--player-bg)" }}>
          <div className="flex justify-center"><LampFlame size={96} /></div>
          <p className="font-ui text-xs font-bold tracking-widest uppercase mt-2" style={{ color: "var(--player-accent)" }}>{t("eb_title")}</p>
          <h1 className={`${lang === "si" ? "font-si" : "font-en"} text-3xl sm:text-4xl font-bold mt-1`} style={{ color: "var(--player-text)" }}>{greeting(settings, lang)} 🪔</h1>
          <p className={`${lang === "si" ? "font-si" : "font-en"} text-lg mt-4 leading-relaxed max-w-xl mx-auto`} style={{ color: "rgba(255,255,255,0.82)" }}>{blessingLine(settings.tradition, lang)}</p>
          <button onClick={light} disabled={!lamp.length} data-testid="evening-light-button" className="mt-7 inline-flex items-center gap-2 h-14 px-8 rounded-full font-ui text-lg font-bold transition-transform active:scale-95 sk-glow disabled:opacity-50" style={{ background: "var(--player-accent)", color: "var(--player-bg)" }}>
            <Flame size={22} fill="currentColor" /> {t("eb_light")}
          </button>
        </div>

        {/* Settings */}
        <div className="rounded-3xl p-6 mt-6 space-y-4" style={{ background: "var(--card)", border: "2px solid var(--border)" }}>
          <h2 className="font-ui text-xl font-bold flex items-center gap-2" style={{ color: "var(--text)" }}><Moon size={20} /> {t("eb_settings")}</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="font-ui text-sm font-bold" style={{ color: "var(--text-2)" }}>{t("eb_name")}</span>
              <input data-testid="evening-name" value={settings.name} onChange={(e) => setSettings({ ...settings, name: e.target.value })} className="mt-1 w-full h-12 px-4 rounded-xl font-ui outline-none" style={inputStyle} />
            </label>
            <label className="block">
              <span className="font-ui text-sm font-bold" style={{ color: "var(--text-2)" }}>{t("eb_tradition")}</span>
              <select data-testid="evening-tradition" value={settings.tradition} onChange={(e) => setSettings({ ...settings, tradition: e.target.value })} className="mt-1 w-full h-12 px-4 rounded-xl font-ui outline-none" style={inputStyle}>
                {TRADITIONS.map((tr) => <option key={tr} value={tr}>{t(`trad_${tr}`)}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="font-ui text-sm font-bold" style={{ color: "var(--text-2)" }}>{t("eb_time")}</span>
              <input data-testid="evening-time" type="time" value={settings.time} onChange={(e) => setSettings({ ...settings, time: e.target.value })} className="mt-1 w-full h-12 px-4 rounded-xl font-ui outline-none" style={inputStyle} />
            </label>
            <label className="flex items-center gap-3 cursor-pointer mt-6">
              <input data-testid="evening-enabled" type="checkbox" checked={settings.enabled} onChange={(e) => setSettings({ ...settings, enabled: e.target.checked })} className="w-6 h-6 accent-amber-600" />
              <span className="font-ui font-semibold text-sm" style={{ color: "var(--text)" }}>{t("eb_enable")}</span>
            </label>
          </div>
          <button onClick={save} data-testid="evening-save" className="h-12 px-6 rounded-xl font-ui font-bold flex items-center gap-2" style={{ background: "var(--accent)", color: "#fff" }}><Save size={18} /> {t("eb_save")}</button>
        </div>

        {/* Today's lamp */}
        <div className="mt-8">
          <h2 className={`${lang === "si" ? "font-si" : "font-en"} text-2xl font-bold mb-4`} style={{ color: "var(--text)" }}>{t("eb_todays_lamp")}</h2>
          <div className="space-y-3">
            {lamp.map((song, i) => <SongRow key={song.id} song={song} index={i} list={lamp} />)}
          </div>
        </div>

        <Link to="/rooms/sindu-kamare" className="flex items-center justify-center gap-2 font-ui text-sm font-semibold mt-8" style={{ color: "var(--accent)" }}><ArrowLeft size={16} /> {t("back_room")}</Link>
      </div>
      {current && <div style={{ height: 8 }} />}
      <PlayerBar />
    </div>
  );
}
